import React, { useState } from 'react';
import toast from 'react-hot-toast';

// Bulk / CSV import แบบ Replay Transaction Chain (IMPORT_REPLAY_PREVIEW):
// ข้อความที่อ่านรู้เรื่องอยู่ลึกที่ replay.error.details.userThaiMessage (ข้อความบนสุดเป็นแค่ "มีบางรายการไม่สำเร็จ")
// รองรับทั้ง error ที่ผ่าน handleApiError (error.details.replay) และ body ดิบ (data.replay / error.details.replay)
const findReplay = (src: any): any =>
  src?.replay || src?.details?.replay || src?.data?.replay || src?.error?.details?.replay || null;

export const getReplayErrorMessages = (src: any): string[] => {
  const replay = findReplay(src);
  if (!replay) return [];
  const main = replay.error?.details?.userThaiMessage || replay.error?.th_message || replay.firstAffectedTransaction?.th_reason;
  if (main) return [main];
  return (Array.isArray(replay.affectedTransactions) ? replay.affectedTransactions : [])
    .map((t: any) => t?.th_reason || t?.reason)
    .filter((m: any): m is string => !!m);
};

// แถวที่พังจากการ Replay → { index: ข้อความไทย } ใช้ไฮไลต์แถวในคิว
export const getReplayRowErrors = (src: any): Record<number, string> => {
  const replay = findReplay(src);
  const rows: Record<number, string> = {};
  (Array.isArray(replay?.affectedTransactions) ? replay.affectedTransactions : []).forEach((t: any) => {
    if (typeof t?.index === 'number' && (t.th_reason || t.reason)) rows[t.index] = t.th_reason || t.reason;
  });
  return rows;
};

// ดึงข้อความภาษาไทยของแต่ละแถวจาก error.details.errors[] (รองรับหลายแถว)
// ถ้าไม่มี errors[] ให้ fallback เป็น th_message / message ของ error หลัก
export const getApiErrorMessages = (error: any, fallback: string): string[] => {
  const replayMessages = getReplayErrorMessages(error);
  if (replayMessages.length > 0) return replayMessages;

  const rowErrors: any[] = Array.isArray(error?.details?.errors) ? error.details.errors : [];
  const rowMessages = rowErrors
    .map((e) => e?.th_message || e?.thMessage || e?.message)
    .filter((m): m is string => !!m);

  if (rowMessages.length > 0) return rowMessages;
  return [error?.th_message || error?.message || fallback];
};

// ข้อความสำหรับก๊อปไปวางในอีเมลแจ้งทีมงาน
const buildEmailText = (messages: string[], traceId: string) => {
  const lines = [
    'แจ้งปัญหาการทำรายการ',
    '',
    `Trace ID: ${traceId || '-'}`,
    '',
    'รายละเอียดข้อผิดพลาด:',
    ...messages.map((m, i) => (messages.length > 1 ? `${i + 1}. ${m}` : m)),
  ];
  return lines.join('\n');
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // fallback สำหรับ browser ที่ไม่รองรับ clipboard API (เช่น ไม่ใช่ https)
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  }
};

const ApiErrorToastContent: React.FC<{
  toastId: string;
  messages: string[];
  traceId: string;
}> = ({ toastId, messages, traceId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(buildEmailText(messages, traceId));
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold">ทำรายการไม่สำเร็จ</span>
        <button
          onClick={() => toast.dismiss(toastId)}
          className="text-gray-400 hover:text-gray-700 leading-none"
          aria-label="ปิด"
        >
          &times;
        </button>
      </div>

      <ul className={`space-y-1 ${messages.length > 1 ? 'list-decimal pl-4' : ''}`}>
        {messages.map((m, i) => (
          <li key={i}>{m}</li>
        ))}
      </ul>

      {traceId && (
        <div className="text-xs text-gray-500 break-all">Trace ID: {traceId}</div>
      )}

      <button
        onClick={handleCopy}
        className={`mt-1 w-full py-1.5 rounded-md border text-xs font-semibold transition-colors ${
          copied
            ? 'bg-green-50 border-green-300 text-green-700'
            : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100 active:bg-gray-200'
        }`}
      >
        {copied ? '✅ คัดลอกแล้ว' : '📋 คัดลอกข้อมูลเพื่อแจ้งทีมงาน'}
      </button>
    </div>
  );
};

// แสดง toast error (สไตล์เดียวกับ toast.error ปกติ) พร้อมรายละเอียดรายแถว + Trace ID + ปุ่มคัดลอก
export const showApiErrorToast = (error: any, fallback = 'เกิดข้อผิดพลาดในการทำรายการ') => {
  const messages = getApiErrorMessages(error, fallback);
  const traceId: string = error?.traceId || error?.meta?.traceId || '';

  toast.error(
    (t) => <ApiErrorToastContent toastId={t.id} messages={messages} traceId={traceId} />,
    { duration: 15000 }
  );
};
